/* The two image assets a company profile carries, and the maths behind positioning them. */

export const ASSET_SPECS = {
  banner: {
    key: "banner",
    /* The section's own wording. */
    label: "Company Banner",
    changeLabel: "Change Banner",
    /* The dialog's. */
    title: "Update Banner",
    cta: "Set banner",
    ratio: 4,
    ratioLabel: "Aspect ratio 4 : 1",
    minWidth: 1200,
    minHeight: 300,
    maxBytes: 5 * 1024 * 1024,
    maxLabel: "PNG / JPG · max 5MB",
    /* Which company field it writes, and the S3 path + category the live
       upload uses — transcribed from handleSubmit. */
    field: "banner",
    uploadPath: "banner",
    category: "company_banner",
  },
  logo: {
    key: "logo",
    label: "Company Logo",
    changeLabel: "Change Logo",
    title: "Update Logo",
    cta: "Set logo",
    ratio: 1,
    ratioLabel: "Aspect ratio 1 : 1 (square)",
    minWidth: 400,
    minHeight: 400,
    maxBytes: 2 * 1024 * 1024,
    maxLabel: "PNG / JPG · max 2MB",
    field: "logo",
    uploadPath: "logo",
    category: "company_logo",
  },
};

export const sizeLabel = (spec) => `${spec.minWidth} × ${spec.minHeight}px min`;

/* The line under each preview, as the design words it. */
export const recommendationLine = (spec) =>
  `Recommended ${spec.ratio === 1 ? "1:1" : `${spec.ratio}:1`} · ` +
  `${spec.minWidth} × ${spec.minHeight}px · PNG or JPG, ` +
  `max ${Math.round(spec.maxBytes / (1024 * 1024))}MB.`;

/* A warning, not a rejection — see the note above. */
export const sizeWarning = (file, spec) => {
  if (!file || !spec) return null;
  if (file.size <= spec.maxBytes) return null;
  const mb = (file.size / (1024 * 1024)).toFixed(1);
  const max = Math.round(spec.maxBytes / (1024 * 1024));
  return `This image is ${mb}MB, over the ${max}MB we recommend. It will still be uploaded, but may load slowly on your profile.`;
};

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 3;

/* How far the image may be dragged before its edge would leave the frame. */
export const offsetBounds = ({ zoom = 1, frameWidth, frameHeight, naturalWidth, naturalHeight }) => {
  if (!frameWidth || !frameHeight || !naturalWidth || !naturalHeight) {
    return { x: 0, y: 0 };
  }
  /* Cover fit: the scale that leaves no gap on either axis. */
  const cover = Math.max(frameWidth / naturalWidth, frameHeight / naturalHeight);
  const drawnWidth = naturalWidth * cover * zoom;
  const drawnHeight = naturalHeight * cover * zoom;

  return {
    x: Math.max(0, (drawnWidth - frameWidth) / 2),
    y: Math.max(0, (drawnHeight - frameHeight) / 2),
  };
};

export const clampOffset = (offset, bounds) => ({
  x: Math.min(bounds.x, Math.max(-bounds.x, offset?.x || 0)),
  y: Math.min(bounds.y, Math.max(-bounds.y, offset?.y || 0)),
});

export const clampZoom = (zoom) =>
  Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number(zoom) || ZOOM_MIN));

/* Whether the viewer actually moved anything. */
export const hasCropEdits = ({ zoom = 1, offset = { x: 0, y: 0 } }) =>
  clampZoom(zoom) !== ZOOM_MIN || Math.round(offset.x) !== 0 || Math.round(offset.y) !== 0;

/* The source rectangle to copy out of the image, given the frame the viewer
   positioned it in. Pure; the canvas work that uses it is in cropImage.js. */
export const sourceRect = ({
  zoom = 1,
  offset = { x: 0, y: 0 },
  frameWidth,
  frameHeight,
  naturalWidth,
  naturalHeight,
}) => {
  const cover = Math.max(frameWidth / naturalWidth, frameHeight / naturalHeight);
  const scale = cover * zoom;

  /* The frame, expressed in the source image's own pixels. */
  const width = frameWidth / scale;
  const height = frameHeight / scale;

  /* The offset is applied to the drawn image, so it moves the window the
     other way, and in source pixels. */
  const x = (naturalWidth - width) / 2 - offset.x / scale;
  const y = (naturalHeight - height) / 2 - offset.y / scale;

  return {
    x: Math.max(0, Math.min(naturalWidth - width, x)),
    y: Math.max(0, Math.min(naturalHeight - height, y)),
    width,
    height,
  };
};

/* The output size: the recommended dimensions, but never upscaled past what
   the source can actually supply. */
export const outputSize = (spec, rect) => {
  const width = Math.min(spec.minWidth, Math.round(rect.width));
  return { width, height: Math.round(width / spec.ratio) };
};
